import { useMemo } from "react";
import type { QuizQuestion } from "@shared/types";
import { useUpdateProfile, useInvalidateProfile } from "../lib/api";
import { useAllQuizQuestions } from "../data/quiz";
import { ProfileField, ProfileSection } from "./profile/ProfileField";

/**
 * The "Basic info" card. Education and Work are net-new free-text profile
 * fields; Pronouns / Gender / Sexuality / Relationship reuse the existing quiz
 * questions (`pronouns`, `genderIdentity`, `sexualOrientation`,
 * `relationshipStatus`) so we don't duplicate those option sets — they're saved
 * straight back into quizAnswers.
 */
export function ProfileBasicInfoSection({
  education,
  work,
  pronouns,
  genderIdentity,
  sexualOrientation,
  relationshipStatus,
}: {
  education: string | null;
  work: string | null;
  pronouns: string[];
  genderIdentity: string[];
  sexualOrientation: string[];
  relationshipStatus: string | null;
}) {
  const updateProfile = useUpdateProfile();
  const invalidate = useInvalidateProfile();
  const allQuestions = useAllQuizQuestions();

  const byId = useMemo(() => {
    const map = new Map<string, QuizQuestion>();
    for (const q of allQuestions) map.set(q.id, q);
    return map;
  }, [allQuestions]);

  const save = (patch: Parameters<typeof updateProfile.mutate>[0]) =>
    updateProfile.mutate(patch, { onSuccess: () => invalidate() });
  const saveAnswer = (id: string, value: string | string[]) =>
    save({ quizAnswers: { [id]: value } as any });

  const q = (id: string) => byId.get(id);

  return (
    <ProfileSection title="Basic info" layout="grid">
      <ProfileField
        kind="text"
        label="Education"
        sheetTitle="Add education"
        value={education}
        placeholder="Where did you study?"
        onSave={(v) => save({ education: v })}
      />
      <ProfileField
        kind="text"
        label="Work"
        sheetTitle="Add work"
        value={work}
        placeholder="What do you do?"
        onSave={(v) => save({ work: v })}
      />

      {q("pronouns") && (
        <ProfileField
          kind="options"
          label="Pronouns"
          question={q("pronouns")!}
          value={pronouns}
          onSave={(v) => saveAnswer("pronouns", v)}
        />
      )}
      {q("genderIdentity") && (
        <ProfileField
          kind="options"
          label="Gender"
          question={q("genderIdentity")!}
          value={genderIdentity}
          onSave={(v) => saveAnswer("genderIdentity", v)}
        />
      )}
      {q("sexualOrientation") && (
        <ProfileField
          kind="options"
          label="Sexuality"
          question={q("sexualOrientation")!}
          value={sexualOrientation}
          onSave={(v) => saveAnswer("sexualOrientation", v)}
        />
      )}
      {q("relationshipStatus") && (
        <ProfileField
          kind="options"
          label="Relationship"
          question={q("relationshipStatus")!}
          value={relationshipStatus}
          onSave={(v) => saveAnswer("relationshipStatus", v)}
        />
      )}
    </ProfileSection>
  );
}
